import { api, ApiError } from '../lib/api.js';
import type { MissingChild } from './scan.js';
import type { PartStationUpdates } from './faults.js';
import type { TreeCtxPart } from '../lib/partTree.js';
import type { OtherKioskKind, StationPick } from '@edal-tracker/shared';

export type ModelPartStatus = {
  partNumber: string; status: 'done' | 'work' | 'issue' | 'todo';
  stageName: string | null; departmentName: string | null; reason: string | null;
};

export type StationTreeContext = {
  projects: { number: string; name: string | null; hasImage: boolean }[];
  parts: TreeCtxPart[];
};

export type StationPartRow = {
  partId: string;
  partNumber: string;
  projectNumber: string;
  /**
   * תאריך האספקה החי של הפרויקט, וכמה ימים נותרו — שלילי = איחור,
   * `null` = אין תאריך (והמסך אומר זאת במילים, לא 0).
   *
   * מחושב בשרת. שני חישובי ימים באותה מערכת הם שני מספרים שונים על אותו
   * מסך ביום שאחד מהם ישתנה.
   */
  deliveryDate: string | null;
  daysLeft: number | null;
  name: string | null;
  quantity: number;
  revision: string | null;
  thumbnailUrl: string | null;
  stampedPdfUrl: string | null;
  currentSequenceIndex: number | null;
  currentStageName: string | null;
  currentDepartmentId: string | null;
  currentDepartmentName: string | null;
  isComplete: boolean;
  /**
   * „הסתיים אצלי" — כל שלבי המחלקה/ות של העמדה במסלול מלאים, או המסלול גמור
   * (מנכ"ל 03/11/2026). לא ב„החלקים שלי" ולא בתור — רק בפרויקט, מקופל.
   */
  doneHere?: boolean;
  /** נעוץ לראש התור בידי מנהל עבודה. null = לא נעוץ. */
  priorityRank: number | null;
  /**
   * „מתוכנן להיום" / „לא בוצע אתמול" (אלי 29/09/2026) — נבחר למחלקה של העמדה
   * בלוח הקיבולת. השרת כבר ממיין אותם לראש; חסר/null = לא נבחר.
   */
  dayPlan?: 'CARRIED' | 'TODAY' | null;
  inSelectedDept: boolean;
  deptSequenceIndex: number | null;
  /**
   * רתך פתח בן בלי ריתוך — השעון ירוץ על ריתוך האבא (אלי 03/10/2026).
   * `childOpenStages` = שלבי הבן שלא הושלמו: סימון, לא חסימה.
   */
  weldViaParent?: {
    parentPartId: string; parentPartNumber: string; sequenceIndex: number; stageName: string;
    childOpenStages: string[];
  } | null;
  /**
   * שם השלב של **העובד** — לא של המחלקה שמחזיקה בחלק.
   *
   * ⚠ בלעדיו המסך שאל על הצ'קליסט של `currentStageName`, שהוא השלב
   * שבו החלק עומד — ובעבודה במקביל זה השלב של **מישהו אחר**.
   * השרת בודק את שלב העובד, ולכן השניים לא הסכימו מעולם.
   */
  deptStageName: string | null;
  /**
   * למה החלק חסום — מוצג על הכרטיס לפני שהעובד הולך לעמדה.
   *
   * מכסה שינוי הנדסי פתוח ואי-התאמה חוסמת בלבד. הסמכת מכונה וצ׳קליסט תלויים
   * במה שייבחר באותו רגע, ולכן **היעדר שבב אינו אומר „פנוי"**.
   */
  blockedKind: 'ECO' | 'NC' | 'HOLD' | null;
  blockedReason: string | null;
  /** אי-התאמה חוסמת פתוחה בשלב הזה — סימון אדום בלי חסימה (אלי 31/10/2026). */
  ncOpen?: string | null;
  /**
   * „חלקים חסרים" — הצאצאים שטרם הגיעו (27/09/2026).
   *
   * ⚠ **אינו** חסימה: אלי הכריע „לאפשר חופשי, עם סימון „חלקים חסרים""; העובד
   * יכול להתחיל, והסימון קיים כדי שרואים אותו מיד. מערך ריק = אין חסר.
   */
  missingChildren: MissingChild[];
  prevDepartmentName: string | null;
  prevWorkerName: string | null;
  nextDepartmentName: string | null;
  targetStageName?: string | null;
  arrivesAfterStageName?: string | null;
  stagesToGo?: number | null;
};

/**
 * שותף על שעון עבודה.
 *
 * ⚠ טיפוס אחד לשלושת הקוראים, ולא שלוש חתימות inline —
 * [[edal-client-type-drops-fields]]: שלוש הצורות היו כתובות בנפרד, ולכן
 * `photoUrl` שהשרת מחזיר היה נעלם מהטיפוס בכל שלושתן. שדה שהטיפוס אינו
 * מכיר הוא שדה שהמסך אינו יכול להציג, גם כשהוא מגיע בתשובה.
 */
export type StationCoWorker = {
  id: string; name: string; employeeNumber: string;
  /** כתובת מוגשת (`/api/scan/employee-photo/:id`), לא נתיב דיסק. */
  photoUrl: string | null;
};

export type IncomingGroup = {
  sourceDepartmentId: string | null;
  sourceDepartmentName: string;
  count: number;
  parts: StationPartRow[];
};

export type StationStageRow = {
  sequenceIndex: number;
  stageName: string;
  departmentName: string | null;
  /** `skipped` — דולג במצב הטמעה, ממתין לדיווח 07:00 (אלי 05/10). */
  status: 'done' | 'current' | 'upcoming' | 'skipped';
  unitsCompleted: number;
  /** היעד של השלב: הכמות ועוד מה שנפסל אחריו ויש להחליף. */
  target: number;
  /**
   * דקות שכבר נרשמו על השלב — לפי `StageProgress.totalMinutes`.
   *
   * אלי, 17/09/2026: „יהיה לו כתוב כמה זמן כבר עבדו על החלק באותה מחלקה
   * ממנה הוא מושך." מנהל שמאשר משיכה מחליט על שעות של אנשים אחרים.
   */
  minutes: number;
  doneByName: string | null;
  doneAt: string | null;
  /** דקות תקן ליחידה — להצעת חלוקת הזמן כששכחו לעבור בין שלבים. */
  stdMinutes?: number | null;
  /** הרכבה שצמודה לפני ריתוך — השרת סוגר אותה עם סיום הריתוך (אלי 03/10). */
  closesWithWelding?: boolean;
};

export type StationProcess = {
  partId: string;
  partNumber: string;
  quantity: number;
  stages: StationStageRow[];
};

export type StationTask = {
  id: string;
  title: string;
  details: string | null;
  projectNumber: string | null;
  scheduledDate: string;
  dueDate: string;
  overdue: boolean;
};

export type QualityMark = {
  id: string;
  index: number;
  x: number; y: number; w: number; h: number; // normalized (0..1), top-left origin
  status: 'PENDING' | 'VERIFIED';
  verifiedByName: string | null;
  verifiedAt: string | null;
};

export type FamilyNode = {
  partId: string;
  partNumber: string;
  name: string | null;
  quantity: number;
  thumbnailUrl: string | null;
  relativeDepth: number;
  isTarget: boolean;
  isComplete: boolean;
  inWork: boolean;
  currentStageName: string | null;
  currentDepartmentName: string | null;
  stagesDone: number;
  stagesTotal: number;
  stages: { stageName: string; departmentName: string | null; status: 'done' | 'current' | 'upcoming' }[];
  /** „חלקים חסרים" — הצאצאים של הצומת שטרם הגיעו (27/09/2026). */
  missingChildren: MissingChild[];
};

export type StationFamily = {
  projectNumber: string;
  ancestors: FamilyNode[];
  target: FamilyNode;
  descendants: FamilyNode[];
};

/**
 * עץ הפרויקט למסך הבחירה. `openable` — ההכרעה היחידה שהמסך קורא:
 * DONE הושלם (הרכבה גמורה — לעיון, רק עם חלק פתוח) · BLOCKED ECO/עצירה/אי-התאמה ·
 * ELSEWHERE לייזר/גמר — מבוצע בקיוסק אחר · ALONE דורש משיכה — נפתח לבד · OK.
 */
export type OpenTreeNode = {
  partId: string; partNumber: string; name: string | null; quantity: number;
  parentId: string | null; thumbnailUrl: string | null;
  currentStageName: string | null; currentDepartmentName: string | null;
  /** השלב הנוכחי במחלקה שנבחרה בעמדה — „שלי קודם". */
  mine?: boolean;
  otherKiosk?: OtherKioskKind | null;
  /** הכלל האחד, בשרת: `stationPick` (shared) — לייזר/גמר לא; מצב הטמעה — חופשי. */
  openable: StationPick; reason: string | null;
};
export type OpenTree = { projectNumber: string; onboarding: boolean; maxParallel: number; nodes: OpenTreeNode[] };

export type OpenSessionRow = {
  sessionId: string;
  workerName: string;
  /** Internal id, not the badge number — see openSessionsForPart on the server. */
  employeeId: string;
  stageName: string;
  sequenceIndex: number;
  startedAt: string;
  elapsedMinutes: number;
  /** „אני אבצע את השלב הקודם": השלב של העובד שאליו יעבור. */
  selfPriorForSeq?: number | null;
};

/** עבודה פתוחה של עובד — מה שצריך להיסגר לפני שמחליפים אותו. */
export type MyOpenSession = {
  sessionId: string; partId: string; partNumber: string; partName: string | null;
  stageName: string; startedAt: string; quantity: number; unitsDone: number;
  /** עלתה רוויזיה מאז שהשעון נפתח — אלי, 24/09/2026: „התראה גדולה וברורה". */
  revisionChanged: boolean;
  fromRevision: string | null;
  toRevision: string | null;
  /** „מחוץ למחלקה" — עובד ממחלקה אחרת, בלי אישור מנהל. מסומן, לא נחסם (U-0924-08). */
  outOfDepartment?: boolean;
};

/** מחלקה כפי שהקיוסק מקבל אותה — עם השם הרוסי כשיש. */
export type StationDepartment = { id: string; name: string; nameRu: string | null };

/**
 * שם המחלקה בשפה שהמסך נקרא בה כרגע.
 *
 * אלי, 09/09/2026: „רק בתוך הקיוסק או המערכת התקנות.“ במסכי המשרד השם
 * נשאר עברי תמיד, ולכן הפונקציה הזאת חיה כאן ולא ב-i18n: היא שאלה של
 * הקיוסק ולא של המערכת.
 *
 * חסר תרגום = השם העברי. מחלקה שלא תורגמה נראית בדיוק כמו היום.
 */
export function deptName(d: { name: string; nameRu?: string | null }, lang: string): string {
  return lang === 'ru' && d.nameRu ? d.nameRu : d.name;
}

/** חומר ההדרכה כפי שהקיוסק מקבל אותו — בלי חלפים, בלי מחירים. */
export type StationLearningDoc = {
  id: string;
  /** MANUAL | SCHEMATIC | ASSEMBLY | VIDEO | PHOTO */
  kind: string;
  title: { text: string; fallback: boolean };
  mime: string | null;
  page: number | null;
};

/** `GET /api/scan/station/cut-list` — ראה `cut-list-drawing.ts` בשרת. */
export type StationCutList = {
  partNumber: string;
  fileName: string | null;
  uploadedAt: string | null;
  multiplier: number;
  lines: Array<{
    id: string; itemNo: string | null; profile: string; sku: string | null;
    lengthMm: number; quantity: number; sourceQuantity: number | null;
    angle1Deg: number; angle2Deg: number; cutCount: number;
  }>;
};

export type StationLearningItem = {
  modelId: string;
  name: { text: string; fallback: boolean };
  manufacturer: string;
  matchedOn: string | null;
  counts: { docs: number; plan: number; faqs: number };
  docs: StationLearningDoc[];
};

export const stationApi = {
  departments: () => api<StationDepartment[]>('/api/scan/station/departments'),
  parts: (opts: { departmentId?: string; search?: string; withDone?: boolean }) => {
    const q = new URLSearchParams();
    if (opts.departmentId) q.set('departmentId', opts.departmentId);
    if (opts.search) q.set('search', opts.search);
    if (opts.withDone) q.set('withDone', '1');
    return api<StationPartRow[]>(`/api/scan/station/parts?${q.toString()}`);
  },
  /**
   * מק״ט אחד שנסרק או הוקלד — ⚠ **התאמה מדויקת, מהשרת**.
   *
   * `parts()` הוא `contains`: סריקת `260025.0.1` מחזירה גם את `260025.0.11`.
   * הכלל „רק התאמה מדויקת היא השרטוט שנסרק" יושב בשרת ולא כאן, כי בצד
   * הלקוח הוא כבר היה קיים בשלושה מצבים שונים — פעם נכון, פעם רגיש לרישיות,
   * ובקיוסק אי-ההתאמות בכלל לא. השרת גם מכריע בין פרויקט לתת-פרויקט, ומסרב
   * בהודעה שאומרת מה לעשות (בוטל / נסגר / לא בעבודה).
   *
   * ⚠ „האם זה במחלקה שלי" נקרא מ-`part.inSelectedDept` שכבר קיים על השורה,
   * ולא משדה שני בתשובה — חלק ממחלקה אחרת הוא מסלול משיכה קיים, לא סירוב.
   */
  partLookup: (number: string, departmentId?: string) => {
    const q = new URLSearchParams({ number });
    if (departmentId) q.set('departmentId', departmentId);
    return api<{ part: StationPartRow }>(`/api/scan/station/part-lookup?${q.toString()}`);
  },
  process: (partId: string) =>
    api<StationProcess>(`/api/scan/station/process?partId=${encodeURIComponent(partId)}`),
  // Co-workers on an active session (own-part auto-start hides the pre-start picker).
  sessionCoWorkers: (sessionId: string) =>
    api<StationCoWorker[]>(`/api/scan/station/session/coworkers?sessionId=${encodeURIComponent(sessionId)}`),
  // `parallel` — השותף רשאי להיות כבר על שרטוט אחר (עד 4, אלי 30/09). נשלח רק מעמדת העובד.
  addCoWorker: (sessionId: string, callerEmployeeNumber: string, coWorkerNumber: string, parallel?: boolean) =>
    api<StationCoWorker[]>('/api/scan/station/session/coworker', {
      method: 'POST',
      body: { sessionId, callerEmployeeNumber, coWorkerNumber, ...(parallel ? { parallel: true } : {}) },
    }),
  // ── "עדיין כאן?" ────────────────────────────────────────────────────────
  //
  // Both endpoints were built and neither had a caller, while the sweep went
  // on closing sessions at two hours and writing "(נסגר אוטומטית — ללא
  // פעולה)". So a worker on a long machining cycle had their clock closed with
  // no warning shown and no button to say otherwise — the exact nudge the
  // server comment promises.
  sessionIdle: (sessionId: string) =>
    api<{
      warned: boolean; idleMinutes: number;
      /** שניות שנותרו לענות על ״טוק טוק״ — null כשלא נשאל. */
      secondsLeft: number | null;
      /** השעון כבר נסגר בשרת (סוחף / סיום יום / מנהל) — העמדה יוצאת מהעובד. */
      gone?: boolean;
    }>(`/api/scan/station/session/idle?sessionId=${encodeURIComponent(sessionId)}`),
  /**
   * לא ענה בזמן — לסגור.
   *
   * Eli, 2026-09-02: "אם הוא לא לחץ, אחרי 60 שניות הסשן נסגר." The screen shows
   * the countdown, so the screen is what closes it when it ends; the server
   * verifies the sixty seconds really passed before it does anything.
   */
  idleClose: (sessionId: string) =>
    api<{ closed: boolean; minutes: number }>('/api/scan/station/session/idle-close', {
      method: 'POST', body: { sessionId },
    }),
  /**
   * סימן חיים על שעון פתוח — ושתי צורות שלו.
   *
   * `ATTENDED` (ברירת מחדל) — מעשה מכוון של אדם. מאפס גם את החיוב וגם את
   * שאלת הנוכחות.
   *
   * `VIEWING` — שרטוט מוצג בעמדת העובד והלשונית גלויה, בלי שאיש נגע בכלום.
   * אלי, 27/09/2026: „בכל זמן נתון שהעובד רואה שרטוט במערכת, המערכת תחשיב
   * כזמן עבודה במסך עמדת עובד." ⚠ אינו מאפס את 60 הדקות, ואחרי ש„טוק טוק"
   * נשאל הוא אינו עושה כלום — אחרת שרטוט פתוח היה עונה על השאלה לנצח.
   */
  stillHere: (sessionId: string, employeeNumber: string, kind: 'ATTENDED' | 'VIEWING' = 'ATTENDED') =>
    api<{ ok: true }>('/api/scan/station/session/still-here', { method: 'POST', body: { sessionId, employeeNumber, kind } }),
  /**
   * מה פתוח אצל העובד הזה — בכל החלקים.
   *
   * Eli, 2026-09-02: "ברגע שהוא לוחץ החלף עובד, הוא צריך להחליף עובד." התנתק
   * cleared the screen and left the session running, so the next worker badged
   * in while the previous man's clock was still counting against him.
   */
  myOpenSessions: (employeeNumber: string) =>
    api<MyOpenSession[]>(`/api/scan/station/my-open-sessions?employeeNumber=${encodeURIComponent(employeeNumber)}`),
  removeCoWorker: (sessionId: string, callerEmployeeNumber: string, coWorkerNumber: string) =>
    api<StationCoWorker[]>('/api/scan/station/session/coworker/remove', { method: 'POST', body: { sessionId, callerEmployeeNumber, coWorkerNumber } }),
  family: (partId: string) =>
    api<StationFamily>(`/api/scan/station/family?partId=${encodeURIComponent(partId)}`),
  /** מסך הבחירה אחרי סריקה — עץ הפרויקט + „אפשר לפתוח מכאן?" (אלי 04/10/2026). */
  openTree: (partId: string, departmentId?: string | null) =>
    api<OpenTree>(`/api/scan/station/open-tree?partId=${encodeURIComponent(partId)}${departmentId ? `&departmentId=${encodeURIComponent(departmentId)}` : ''}`),
  /** צבעי מודל ה-3D לפי מק״ט — מדיווחי העמדות (המציג אינו קובע סטטוס). */
  modelStatus: (projectNumber: string) =>
    api<{ parts: ModelPartStatus[] }>(`/api/scan/station/model-status?projectNumber=${encodeURIComponent(projectNumber)}`),
  model3d: (projectNumber: string) =>
    api<{ url: string | null; kind: 'glb' | 'html' | null }>(`/api/scan/station/model?projectNumber=${encodeURIComponent(projectNumber)}`),
  openSessions: (partPayload: string) =>
    api<OpenSessionRow[]>(`/api/scan/station/open-sessions?partPayload=${encodeURIComponent(partPayload)}`),
  /** `minutes` is what the manager says was actually worked; absent = elapsed. */
  closeSession: (sessionId: string, managerCode: string, minutes?: number) =>
    api<{ ok: true }>('/api/scan/station/close-session', { method: 'POST', body: { sessionId, managerCode, ...(minutes !== undefined ? { minutes } : {}) } }),
  /**
   * דקות של מחלקה ש**לא פתחה שעון כלל**.
   *
   * אלי, 17/09/2026: „הם לא סגרו, לא סיימו במחשב, **או לא התחילו במחשב**."
   * ⚠ `closeSession` מכסה את שני הראשונים בלבד — הוא דורש שעון פתוח. זה
   * השלישי, ובלעדיו הדקות של המחלקה הקודמת נופלות לרצפה.
   */
  priorMinutes: (body: {
    partId: string; sequenceIndex: number; minutes: number;
    employeeNumber: string; managerCode: string;
    /** המחלקה שמושכת — השרת מסרב לפני כתיבה כשהשלב שלה כבר מלא. */
    departmentId?: string;
  }) => api<{ ok: true; minutes: number }>('/api/scan/station/prior-minutes', { method: 'POST', body }),
  incoming: (departmentId: string) =>
    api<IncomingGroup[]>(`/api/scan/station/incoming?departmentId=${encodeURIComponent(departmentId)}`),
  /** נופק לך היום בלוח הקיבולת — פרויקט, מחלקה, כמה שעות (אלי 12/10/2026). */
  crewToday: (employeeNumber: string) =>
    api<Array<{ projectNumber: string; projectName: string | null; departmentName: string; hours: number }>>(
      `/api/scan/station/crew-today?employeeNumber=${encodeURIComponent(employeeNumber)}`),
  myAssignments: (employeeNumber: string) =>
    api<StationPartRow[]>(`/api/scan/station/assignments?employeeNumber=${encodeURIComponent(employeeNumber)}`),
  myTasks: (employeeNumber: string) =>
    api<StationTask[]>(`/api/scan/station/tasks?employeeNumber=${encodeURIComponent(employeeNumber)}`),
  /** U-1010-06 — הכלים מהמחסן שאצל העובד; קריאה בלבד. */
  myTools: (employeeNumber: string) =>
    api<{ name: string; sticker: string | null; kind: 'TEMP' | 'PERM'; destination: string | null; issuedAt: string; overdue: boolean }[]>(
      `/api/scan/station/tools?employeeNumber=${encodeURIComponent(employeeNumber)}`),
  completeTask: (employeeNumber: string, taskId: string) =>
    api<{ ok: true }>(`/api/scan/station/tasks/${encodeURIComponent(taskId)}/done`, { method: 'POST', body: { employeeNumber } }),
  /** טבלת החיתוך של השרטוט, לקריאה ליד השרטוט (אלי 02/11/2026). */
  cutList: (partId: string) =>
    api<StationCutList>(`/api/scan/station/cut-list?partId=${encodeURIComponent(partId)}`),
  // Quality marks (yellow-flagged critical dimensions) + verification.
  qualityMarks: (partId: string) =>
    api<QualityMark[]>(`/api/scan/station/quality-marks?partId=${encodeURIComponent(partId)}`),
  /** כמה עמודים בשרטוט הפעיל (אלי 04/10 — דפדוף בין עמודים). */
  pageCount: (partId: string, employeeNumber: string) =>
    api<{ pages: number }>(`/api/scan/station/page-count?partId=${encodeURIComponent(partId)}&employeeNumber=${encodeURIComponent(employeeNumber)}`),
  verifyMark: (id: string, employeeNumber: string) =>
    api<{ ok: true; verifiedByName: string }>(`/api/scan/station/quality-marks/${encodeURIComponent(id)}/verify`, { method: 'POST', body: { employeeNumber } }),
  // Public file URLs (kiosk is unauthenticated — these bypass the gated /data mount).
  /**
   * חומר ההדרכה של הפרויקט, לעמדה.
   *
   * דרך /scan ולא /learning: העמדה היא קיוסק אנונימי, והנתיב שם דורש
   * התחברות. ומכאן חוזרים רק שמות וספירות — לא חלפים ולא מחירים.
   */
  learning: (projectNumber: string, lang?: string) =>
    api<{ items: StationLearningItem[] }>(
      `/api/scan/station/learning?projectNumber=${encodeURIComponent(projectNumber)}${lang === 'ru' ? '&lang=ru' : ''}`),

  /** הקובץ עצמו, דרך הקיוסק — העובד אינו מחובר. */
  learningDocUrl: (docId: string) => `/api/scan/station/learning-doc/${docId}`,

  // ── `v` — הגרסה, כדי שהכתובת תשתנה כשעולה רוויזיה ────────────────────
  //
  // אלי, 24/09/2026: „העובד **לא רואה את A לעולם**." הכתובת `?partId=…`
  // זהה לכל רוויזיה (השרת מגיש את הפעילה), ודפדפן/iframe שזוכר אותה היה
  // מציג את A אחרי ש-B עלה. השרת שולח `no-store`, ו-`v` סוגר גם מטמון
  // שכבר נשמר לפני התיקון.
  /** קיפול התור (U-1003-03): כל חלקי הפרויקטים שבתור — אבות, שמות, שרטוט, גמור. */
  tree: (projectNumbers: string[]) =>
    api<StationTreeContext>(`/api/scan/station/tree?projects=${encodeURIComponent(projectNumbers.join(','))}`),
  /** התמונה שההנדסה העלתה לפרויקט — דרך הקיוסק (404 = אין). */
  projectImageUrl: (projectNumber: string) => `/api/scan/project-image/${encodeURIComponent(projectNumber)}`,
  thumbUrl: (partId: string, v?: number | string | null) =>
    `/api/scan/station/thumb?partId=${encodeURIComponent(partId)}${v != null ? `&v=${encodeURIComponent(String(v))}` : ''}`,
  pdfUrl: (partId: string, v?: number | string | null) =>
    `/api/scan/station/pdf?partId=${encodeURIComponent(partId)}${v != null ? `&v=${encodeURIComponent(String(v))}` : ''}`,
  pageImageUrl: (partId: string, v?: number | string | null) =>
    `/api/scan/station/page-image?partId=${encodeURIComponent(partId)}${v != null ? `&v=${encodeURIComponent(String(v))}` : ''}`,
  /**
   * הגיליון עצמו — ופתיחה שאומרת **למה** כשהיא נדחית.
   *
   * ⚠ `pdfUrl` ב-`window.open` הוא הצורה שכל המסכים המחוברים משתמשים בה,
   * והיא נכונה שם. בקיוסק היא אינה: `assertMayViewDrawing` מסרב 403 לחלק
   * שאינו במחלקת העובד („החלק נמצא כעת בריתוך — יש למשוך אותו באישור
   * מנהל"), והדפדפן פותח לשונית חדשה עם **JSON גולמי**. כלומר דווקא
   * ההודעה שאומרת מה לעשות היא זו שהעובד לא רואה, והוא רואה
   * `{"code":"DRAWING_NOT_YOURS",…}` על מסך מגע ליד מכונה.
   *
   * ולכן הבקשה נעשית כאן, וסירוב יוצא כ-`ApiError` כמו כל קריאה אחרת —
   * `stationErrorText` אומר אותו בשפת העובד, והמסך מציג אותו במקום.
   * מחזירה `blob:` URL שהקורא פותח; הקורא גם משחרר אותו.
   *
   * ⚠ `employeeNumber` נדרש בקיוסק — בלעדיו השער מסרב „יש לסרוק תג לפני
   * פתיחת שרטוט" (ראה `work-gate.ts`). מסך משרדי מחובר אינו שולח אחד.
   */
  drawingBlobUrl: async (
    partId: string, employeeNumber?: string | null, v?: number | string | null,
  ): Promise<string> => {
    const url = `/api/scan/station/pdf?partId=${encodeURIComponent(partId)}`
      + (v != null ? `&v=${encodeURIComponent(String(v))}` : '')
      + (employeeNumber ? `&employeeNumber=${encodeURIComponent(employeeNumber)}` : '');
    let res: Response;
    try {
      res = await fetch(url, { credentials: 'include' });
    } catch {
      throw new ApiError(0, 'NETWORK', 'לא ניתן להגיע לשרת. יש לבדוק את חיבור הרשת ולנסות שוב.');
    }
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as
        { code?: string; message?: string; details?: unknown };
      throw new ApiError(
        res.status, body.code ?? 'NETWORK',
        body.message ?? 'לא ניתן לפתוח את השרטוט. פנה למנהל המשמרת.',
        undefined, body.details,
      );
    }
    return URL.createObjectURL(await res.blob());
  },
  /**
   * תמונת עמוד של הגיליון, דרך אותו שער — לחלון ההגדלה בחיפוש הגמר (01/11/2026).
   * תמונה ולא PDF: בטאבלט אין מציג PDF בתוך מסגרת, והמציג של העמדה מקבל תמונה.
   * כמו `drawingBlobUrl`, סירוב יוצא כ-`ApiError` שאומר מה לעשות, ולא תמונה שבורה.
   * ההוכחה: תג, או שעון פתוח על החלק (`sessionId`, כמו כרטיס על הלוח).
   */
  sheetImageBlobUrl: async (
    partId: string, proof: { employeeNumber?: string | null; sessionId?: string | null }, page = 1,
  ): Promise<string> => {
    const url = `/api/scan/station/page-image?partId=${encodeURIComponent(partId)}`
      + (proof.employeeNumber ? `&employeeNumber=${encodeURIComponent(proof.employeeNumber)}` : '')
      + (proof.sessionId ? `&sessionId=${encodeURIComponent(proof.sessionId)}` : '')
      + (page > 1 ? `&page=${page}` : '');
    let res: Response;
    try {
      res = await fetch(url, { credentials: 'include' });
    } catch {
      throw new ApiError(0, 'NETWORK', 'לא ניתן להגיע לשרת. יש לבדוק את חיבור הרשת ולנסות שוב.');
    }
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as
        { code?: string; message?: string; details?: unknown };
      throw new ApiError(
        res.status, body.code ?? 'NETWORK',
        body.message ?? 'לא ניתן לפתוח את השרטוט. פנה למנהל המשמרת.',
        undefined, body.details,
      );
    }
    return URL.createObjectURL(await res.blob());
  },
  /** כמה עמודים בגיליון — אותה הוכחה כמו `sheetImageBlobUrl`. */
  sheetPageCount: (partId: string, proof: { employeeNumber?: string | null; sessionId?: string | null }) =>
    api<{ pages: number }>(`/api/scan/station/page-count?partId=${encodeURIComponent(partId)}`
      + (proof.employeeNumber ? `&employeeNumber=${encodeURIComponent(proof.employeeNumber)}` : '')
      + (proof.sessionId ? `&sessionId=${encodeURIComponent(proof.sessionId)}` : '')),
  /**
   * „אין שרטוט — אושר לעבוד בלי שרטוט ע״י X" (אלי, 24/09/2026). `waived:
   * false` כשיש לחלק שרטוט פעיל, או שאיש לא אישר לעבוד בלעדיו.
   */
  drawingWaiver: (partId: string) =>
    api<{ waived: boolean; byName: string | null; at: string | null }>(
      `/api/scan/drawing-waiver?partId=${encodeURIComponent(partId)}`),
  // Recent engineering updates on a part → drawing banner + timer stop.
  partFaults: (partId: string, employeeNumber?: string) =>
    api<PartStationUpdates>(
      `/api/scan/station/part-faults?partId=${encodeURIComponent(partId)}`
      + (employeeNumber ? `&employeeNumber=${encodeURIComponent(employeeNumber)}` : ''),
    ),
  /** „כמה הספקת עד שנעצרת" — נכתב לאירוע העצירה עצמו. */
  reportStopped: (scanEventId: string, units: number, employeeNumber: string) =>
    api<{ ok: true; counted: number; stageName: string }>(
      '/api/scan/station/report-stopped',
      { method: 'POST', body: { scanEventId, units, employeeNumber } }),
  /**
   * העצירה שטרם נענתה של העובד — על כל חלק. לקיוסקים שאין בהם חלק פתוח
   * (גמר, סריקה): כשחלק בוטל הכרטיס נעלם, והשאלה „כמה סיימת" נשאלת כאן.
   */
  pendingStop: (employeeNumber: string) =>
    api<{ stopped: (NonNullable<PartStationUpdates['stopped']> & { partNumber: string; remaining?: number }) | null }>(
      `/api/scan/station/pending-stop?employeeNumber=${encodeURIComponent(employeeNumber)}`),
};
